<template>
  <div class="cascader-box">
    <el-cascader
      ref="cascaderRef"
      v-model="schoolIds"
      :options="schoolList"
      placeholder="大区/区域/校区"
      :show-all-levels="false"
      :collapse-tags="true"
      :collapse-tags-tooltip="true"
      clearable
      filterable
      @change="select"
      v-bind="attrs"
      :props="propsConfig"
    />
  </div>
</template>

<script setup>
import { getManageAreaTree, getTeachAreaTree, getManageAreaAllTree, getNotShutdownTree,getRegionTreeAll, getHomePlanTree, getRegionOpenTree, getManageAreaTreePlus} from '@/api/kxmx/index';
import { listRegionAll } from '@/api/education/class';
import { nextTick, onMounted } from 'vue';
const cascaderRef = ref()
const props = defineProps({
  modelValue: {
    type: [String, Array],
  },
  type: {
    type: String, //默认为管理区域 ‘manage’,教学区域‘teach’
    default: 'manage',
  },
  returnType: {
    type: String, //返回数据类型 数组 Array 字符 逗号连接 String 多选有效
    default: 'Array',
  },
  checkAll: {
    type: Boolean,
    default: true,
  },
  additionalSelectorItem: {
    // 额外的校区选项，如增加“无”这种选项。如果传了，会放在选项最前面。
    type: Object, // 如：:additional-selector-item="{label: '无', id: '0'}"
    default: undefined,
  },
  // 筛选列表
  filterList: {
    type: Array,
    default: () => [],
  },

  defaultSingleSelect: { // 单选时生效，是否默认选中第一个校区
    type: Boolean,
    default: false
  },
  presentText:{
    type: String,
    default:""
  },
  level: {
    type: Number,
    default: 3,
  },
});
const emits = defineEmits(['update:modelValue', 'checkedAll', 'change','update:presentText']);
const attrs = useAttrs();

const schoolIds = ref(null);
const schoolList = ref([]);
//报名校区配置
const propsConfig = reactive({
  value: 'id',
  label: 'label',
  multiple: true,
  emitPath: false,
});

watch(
  () => props.modelValue,
  (val) => {
    if (attrs.props) {
      Object.assign(propsConfig, attrs.props);
    }
    if (val) {
      if (propsConfig.multiple) {
        schoolIds.value = Array.isArray(val) ? val : val.split(',');
      } else {
        schoolIds.value = val;
      }
    } else {
      if (propsConfig.multiple) {
        schoolIds.value = [];
      } else {
        schoolIds.value = '';
      }
    }
  },
  {
    deep: true,
    immediate: true,
  }
);
const defVals = ref([]);

onMounted(() => {
  init();
});

function init() {
  getRegionList();
}

/** 查询管理/教学区域数据 */
async function getRegionList() {
  let res = null;
  if (props.type == 'manage') {
    // 有权限管理区域
    res = await getManageAreaTree();
  } else if (props.type == 'manageAll') {
    //无权限管理区域校区
    res = await getManageAreaAllTree();
  } else if (props.type == 'manageRegister') {
    //启用的管理区域校区
    res = await listRegionAll();
  } else if (props.type == 'teach') {
    res = await getTeachAreaTree();
    if (res.data?.length > 0) {
      res.data = [
        {
          id: '001',
          label: '集团校区',
          children: [...res.data],
        },
      ];
    }
  } else if (props.type == 'notShutdown') {
    res = await getNotShutdownTree();
  }else if(props.type == 'hwgAll'){
    res = await getRegionTreeAll()
  } else if (props.type == 'planSchool') { // 计划内校区，用于高管首页
    res = await getHomePlanTree()
  } else if (props.type == 'regionOpenTree') { // 教材专用
    res = await getRegionOpenTree();
  } else if (props.type == 'managePlus') { // 有权限管理区域加待分配校区
    res = await getManageAreaTreePlus();
  }
  if (props.additionalSelectorItem) {
    res.data.unshift(props.additionalSelectorItem);
  }
  const temData =  props.filterList.length === 0 ? res.data : treeFilter(res.data, ({ id }) => props.filterList.includes(id));
  if (props.level ===1) {
    schoolList.value = temData.map(item => {
        item.children = null;
        return item;
    });

  } else if (props.level === 2) {
        schoolList.value = temData.map(item => {
            item.children = item.children?.map(el => {
                el.children = null;
                return el;
            });
        return item;
    });
  } else {
    schoolList.value = temData;
  }
  isCheckAll();
  nextTick(() =>{
    const presentText = cascaderRef.value?.presentText;
    emits('update:presentText',presentText)
  })
}

function treeFilter(tree, func) {
  return tree
    .map((node) => ({ ...node }))
    .filter((node) => {
      node.children = node.children && treeFilter(node.children, func);
      return func(node) || (node.children && node.children.length);
    });
}

function isCheckAll(options = { force: false }) {
  if (props.checkAll == true && propsConfig.multiple && (!props.modelValue?.length || options.force)) {
    if (schoolList.value?.length > 0) {
      getAllValues(toRaw(schoolList.value));
      schoolIds.value = [...defVals.value];
      select(toRaw(schoolIds.value));
      emits('checkedAll'); // 全部选中完成，抛出事件，便于调用方初始化
    }
  }
  if (props.defaultSingleSelect && !propsConfig.multiple && schoolList.value?.length) { // 单选时，默认选中第一个
    const findFirst = (list) => {
      if (list[0].children?.length) {
        return findFirst(list[0].children)
      } else {
        return list[0].id
      }
    }
    const firstId = findFirst(schoolList.value)
    if (firstId) {
      emits('update:modelValue', firstId);
      emits('checkedAll')
    }
  }
}

defineExpose({
  schoolList,
  doCheckAll: isCheckAll, // 全选中函数，便于重置时调用
});

function getAllValues(val) {
  val.forEach((element) => {
    if (element.hasOwnProperty('children') && element.children.length > 0) {
      getAllValues(element.children);
    } else {
      defVals.value.push(element.id);
    }
  });
}
/**选择校区时，获取校区id */
function select(vals) {
  emits('change', vals);
  if (props.returnType == 'Array') {
    emits('update:modelValue', vals);
  } else if (props.returnType == 'String') {
    if (propsConfig.multiple) {
      if (vals.length > 0) {
        emits('update:modelValue', vals.join(','));
      } else {
        emits('update:modelValue', '');
      }
    } else {
      emits('update:modelValue', vals);
    }
    emits('select-change');
  }
  const presentText = cascaderRef.value.presentText;
  emits('update:presentText',presentText)
}
</script>

<style lang="scss" scoped>
.cascader-box {
  :deep(.el-cascader) {
    width: inherit;

    .el-input {
      width: var(--el-input-width) !important;
    }

    .el-cascader__tags {
      flex-wrap: nowrap;

      .el-cascader__search-input {
        min-width: 0px;
      }
    }
  }
}
</style>
